import React, { useState, useEffect, useMemo } from 'react';

const MY_TG_ID = localStorage.getItem('tg_user') || '';
const API_URL_PRODUCTS = 'https://crm.mmachine.pro/api/products';
const API_URL_ASSEMBLIES = 'https://crm.mmachine.pro/api/assemblies';

const toLocalIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function RangeCalendar({ start, end, onSelect, onClose }) {
  const [view, setView] = useState(start ? new Date(start) : new Date());

  const nextM = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));
  const prevM = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));

  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const firstDay = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
  const offset = (firstDay + 6) % 7; 

  const days = [];
  for(let i=0; i<offset; i++) days.push(null);
  for(let i=1; i<=daysInMonth; i++) days.push(new Date(view.getFullYear(), view.getMonth(), i));

  const handleDay = (d) => {
    if(!d) return;
    const dStr = toLocalIso(d);
    
    if (!start || (start && end)) {
      onSelect(dStr, ''); 
    } else {
      const sTime = new Date(start).getTime();
      const clickTime = d.getTime();
      if (clickTime < sTime) {
        onSelect(dStr, start);
        onClose();
      } else {
        onSelect(start, dStr);
        onClose(); 
      }
    }
  };

  const isSelected = (d) => {
    if(!d) return false;
    const dt = toLocalIso(d);
    return dt === start || dt === end;
  };

  const isInRange = (d) => {
    if(!d || !start || !end) return false;
    const t = d.getTime();
    const sT = new Date(start).getTime();
    const eT = new Date(end).getTime();
    return t > sT && t < eT;
  };

  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  return (
    <div className="absolute top-full left-4 mt-2 bg-white border border-slate-200 shadow-2xl rounded-2xl p-5 w-[320px] z-50">
       <div className="flex justify-between items-center mb-4">
         <button onClick={prevM} className="w-8 h-8 rounded hover:bg-slate-100 font-bold text-slate-500 transition-colors">&lt;</button>
         <span className="font-bold text-slate-800 text-sm uppercase tracking-wider">{monthNames[view.getMonth()]} {view.getFullYear()}</span>
         <button onClick={nextM} className="w-8 h-8 rounded hover:bg-slate-100 font-bold text-slate-500 transition-colors">&gt;</button>
       </div>
       <div className="grid grid-cols-7 gap-1 text-center mb-2">
         {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(w => <div key={w} className="text-[10px] font-bold text-slate-400">{w}</div>)}
       </div>
       <div className="grid grid-cols-7 gap-1 text-center">
         {days.map((d, i) => {
            if(!d) return <div key={i} className="w-9 h-9" />;
            const sel = isSelected(d);
            const inR = isInRange(d);
            return (
              <div key={i} onClick={() => handleDay(d)} className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm cursor-pointer transition-all ${sel ? 'bg-indigo-600 text-white font-bold shadow-md scale-105' : (inR ? 'bg-indigo-100 text-indigo-800' : 'text-slate-700 hover:bg-slate-100')}`}>
                {d.getDate()}
              </div>
            )
         })}
       </div>
       <div className="mt-5 border-t border-slate-100 pt-3 flex justify-between items-center">
         <button onClick={() => onSelect('','')} className="text-xs text-slate-400 hover:text-red-500 font-bold transition-colors">Сбросить</button>
         <button onClick={onClose} className="text-xs bg-slate-800 text-white px-3 py-1.5 rounded hover:bg-slate-900 font-bold transition-colors">Готово</button>
       </div>
    </div>
  );
}

export default function DashboardPage() {
  const [products, setProducts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [periodType, setPeriodType] = useState('all'); 
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, asmRes] = await Promise.all([
          fetch(API_URL_PRODUCTS, { headers: { 'tg-id': window.localStorage.getItem('tg_user') || '' } }),
          fetch(API_URL_ASSEMBLIES, { headers: { 'tg-id': window.localStorage.getItem('tg_user') || '' } })
        ]);
        setProducts(await prodRes.json());
        setAssemblies(await asmRes.json());
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatMoney = (amount) => new Intl.NumberFormat('ru-RU').format(amount) + ' ₽';

  const stats = useMemo(() => {
    let pProfit = 0; let pCount = 0; let wItems = 0;
    let bProfit = 0; let bCount = 0; let activeBCount = 0;

    const checkDate = (dateStr) => {
      if (periodType === 'all') return true;
      if (!dateStr) return false;
      
      const d = new Date(dateStr);
      const now = new Date();
      
      if (periodType === 'thisMonth') {
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      }
      if (periodType === 'prevMonth') {
        let pm = now.getMonth() - 1;
        let py = now.getFullYear();
        if (pm < 0) { pm = 11; py--; }
        return d.getFullYear() === py && d.getMonth() === pm;
      }
      if (periodType === 'custom') {
        if (!customStart && !customEnd) return true;
        const dTime = d.getTime();
        const sTime = customStart ? new Date(customStart).getTime() : 0;
        const eTime = customEnd ? new Date(customEnd).getTime() : Infinity;
        return dTime >= sTime && dTime <= eTime + 86400000;
      }
      return true;
    };

    products.forEach(p => {
      if (!p.assembly_id) {
        wItems++; 
        if (p.selling_price > 0) {
          const effectiveDate = p.sale_date || p.created_at;
          if (checkDate(effectiveDate)) {
            pProfit += (p.selling_price - p.purchase_price);
            pCount++;
          }
        }
      }
    });

    assemblies.forEach(a => {
      if (a.sellingPrice > 0) {
        const effectiveDate = a.sale_date || a.created_at;
        if (checkDate(effectiveDate)) {
          bProfit += (a.sellingPrice - a.cost);
          bCount++;
        }
      } else {
        activeBCount++;
      }
    });

    return { totalProfit: pProfit + bProfit, buildsProfit: bProfit, productsProfit: pProfit, soldBuildsCount: bCount, soldProductsCount: pCount, activeBuildsCount: activeBCount, warehouseItemsCount: wItems };
  }, [products, assemblies, periodType, customStart, customEnd]);

  if (isLoading) return <div className="flex justify-center items-center h-64"><p className="text-slate-400 font-medium">Загрузка данных...</p></div>;

  return (
    <div className="max-w-[1400px] mx-auto pb-24 relative px-4 mt-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate-800">Финансовая сводка</h1>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-8 flex flex-wrap gap-4 items-center">
        <span className="text-sm font-bold text-slate-500 uppercase tracking-wider mr-2">Период:</span>
        <div className="flex space-x-2">
          <button onClick={() => { setPeriodType('all'); setShowCalendar(false); }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${periodType === 'all' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>За всё время</button>
          <button onClick={() => { setPeriodType('thisMonth'); setShowCalendar(false); }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${periodType === 'thisMonth' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Этот месяц</button>
          <button onClick={() => { setPeriodType('prevMonth'); setShowCalendar(false); }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${periodType === 'prevMonth' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Прошлый месяц</button>
          <button onClick={() => setPeriodType('custom')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${periodType === 'custom' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Свой период</button>
        </div>
        
        {periodType === 'custom' && (
          <div className="relative ml-4 pl-4 border-l border-slate-200">
            <button onClick={() => setShowCalendar(!showCalendar)} className={`border px-4 py-2 rounded-lg text-sm font-medium hover:border-indigo-400 min-w-[220px] text-left flex justify-between items-center transition-colors ${showCalendar ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-white'}`}>
              <span className={customStart ? 'text-slate-800 font-bold' : 'text-slate-400'}>
                {customStart ? new Date(customStart).toLocaleDateString('ru-RU') : 'Начальная дата'} 
                <span className="mx-2 font-normal text-slate-400">—</span> 
                {customEnd ? new Date(customEnd).toLocaleDateString('ru-RU') : 'Конечная дата'}
              </span>
              <span className="text-slate-400 ml-3 text-lg">📅</span>
            </button>

            {showCalendar && (
              <RangeCalendar 
                start={customStart} 
                end={customEnd} 
                onSelect={(s, e) => { setCustomStart(s); setCustomEnd(e); }} 
                onClose={() => setShowCalendar(false)} 
              />
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -mr-10 -mt-10 z-0"></div>
          <div className="relative z-10">
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Общая чистая прибыль</h2>
            <div className={`text-4xl font-black ${stats.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatMoney(stats.totalProfit)}</div>
            <p className="text-sm text-slate-500 mt-2 font-medium">Всего продано позиций: <span className="text-slate-800 font-bold">{stats.soldBuildsCount + stats.soldProductsCount}</span></p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-10 -mt-10 z-0"></div>
          <div className="relative z-10">
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Прибыль со сборок ПК</h2>
            <div className={`text-3xl font-black ${stats.buildsProfit >= 0 ? 'text-blue-600' : 'text-red-500'}`}>{formatMoney(stats.buildsProfit)}</div>
            <div className="flex justify-between items-center mt-4">
              <div className="text-xs text-slate-500 font-medium">Продано: <span className="text-slate-800 font-bold text-sm">{stats.soldBuildsCount} шт</span></div>
              {periodType === 'all' && <div className="text-xs text-slate-500 font-medium">В работе: <span className="text-blue-600 font-bold text-sm">{stats.activeBuildsCount} шт</span></div>}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-full -mr-10 -mt-10 z-0"></div>
          <div className="relative z-10">
            <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Прибыль с розницы</h2>
            <div className={`text-3xl font-black ${stats.productsProfit >= 0 ? 'text-indigo-600' : 'text-red-500'}`}>{formatMoney(stats.productsProfit)}</div>
            <div className="flex justify-between items-center mt-4">
              <div className="text-xs text-slate-500 font-medium">Продано: <span className="text-slate-800 font-bold text-sm">{stats.soldProductsCount} шт</span></div>
              {periodType === 'all' && <div className="text-xs text-slate-500 font-medium">Свободно на складе: <span className="text-indigo-600 font-bold text-sm">{stats.warehouseItemsCount} шт</span></div>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
          <span className="bg-orange-100 text-orange-500 w-10 h-10 rounded-xl flex items-center justify-center mr-3 text-2xl shadow-sm">🏆</span>
          Лучшие сотрудники
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
           {/* Карточка сотрудника 1 - МАКСИМ */}
           <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:shadow-md transition-all flex flex-col">
             <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500"></div>
             <div className="flex items-center space-x-4 mb-4">
               <img src="/images/maxim.jpg" alt="Максим" className="w-16 h-16 rounded-full object-cover shadow-lg group-hover:scale-110 transition-transform border-4 border-white" />
               <div>
                 <h3 className="text-lg font-bold text-slate-800">Максим</h3>
                 <p className="text-sm text-slate-500 font-medium">Основатель / Продажи</p>
               </div>
             </div>
             {/* FLEX ВМЕСТО GRID */}
             <div className="mt-auto flex justify-between items-start border-t border-slate-100 pt-4">
               <div className="pr-2">
                 <div className="text-[10px] font-bold text-slate-400 uppercase leading-tight">Продаст даже лёд в Гренландию</div>
                 <div className="text-lg font-black text-slate-700 mt-1 whitespace-nowrap">Продажный Гарри</div>
               </div>
               <div className="text-right shrink-0">
                 <div className="text-[10px] font-bold text-slate-400 uppercase">Рейтинг</div>
                 <div className="text-lg font-black text-amber-500">5.0 ⭐</div>
               </div>
             </div>
           </div>

           {/* Карточка сотрудника 2 - Марат */}
           <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:shadow-md transition-all flex flex-col">
             <div className="absolute top-0 left-0 w-2 h-full bg-blue-500"></div>
             <div className="flex items-center space-x-4 mb-4">
               <img src="/images/marat.jpg" alt="Марат" className="w-16 h-16 rounded-full object-cover shadow-lg group-hover:scale-110 transition-transform border-4 border-white" />
               <div>
                 <h3 className="text-lg font-bold text-slate-800">Марат</h3>
                 <p className="text-sm text-slate-500 font-medium">Основатель / Сборки</p>
               </div>
             </div>
             {/* FLEX ВМЕСТО GRID */}
             <div className="mt-auto flex justify-between items-start border-t border-slate-100 pt-4">
               <div className="pr-2">
                 <div className="text-[10px] font-bold text-slate-400 uppercase leading-tight">Соберёт даже мотоциклиста после сильной аварии</div>
                 <div className="text-lg font-black text-slate-700 mt-1 whitespace-nowrap">Компьютерный кутюрье</div>
               </div>
               <div className="text-right shrink-0">
                 <div className="text-[10px] font-bold text-slate-400 uppercase">Рейтинг</div>
                 <div className="text-lg font-black text-amber-500">4.99 ⭐</div>
               </div>
             </div>
           </div>

           {/* Кнопка добавления */}
           <div className="bg-slate-50 rounded-2xl p-6 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-100 hover:border-indigo-300 hover:text-indigo-500 transition-colors cursor-pointer min-h-[180px]">
             <span className="text-4xl mb-2 leading-none">+</span>
             <span className="text-sm font-bold uppercase tracking-wider">Добавить сотрудника</span>
             <span className="text-xs text-slate-400 mt-2 text-center">(Статистика в разработке)</span>
           </div>
        </div>
      </div>

    </div>
  );
}
